import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import { salesRadarService } from '../../services/salesRadarService';
import { formatDistanceToNow, format, parseISO } from 'date-fns';

// Score color helper
const getScoreColor = (score) => {
  if (score >= 80) return '#22c55e';
  if (score >= 60) return '#3b82f6';
  if (score >= 40) return '#f59e0b';
  return '#ef4444';
};

// Stage color helper
const getStageColors = (stage) => {
  const colors = {
    visitor: { bg: '#f3f4f6', text: '#6b7280' },
    lead: { bg: '#dbeafe', text: '#2563eb' },
    mql: { bg: '#e0e7ff', text: '#4f46e5' },
    sql: { bg: '#fae8ff', text: '#a855f7' },
    opportunity: { bg: '#fef3c7', text: '#d97706' },
    customer: { bg: '#dcfce7', text: '#16a34a' },
    churned: { bg: '#fee2e2', text: '#dc2626' },
  };
  return colors[stage] || colors.visitor;
};

// Event icon mapping
const getEventIcon = (eventType) => {
  const icons = {
    page_view: '👁️',
    click: '👆',
    form_submit: '✅',
    download: '📥',
    email_open: '📧',
    email_click: '🔗',
    purchase: '🛒',
    signup: '👤',
    identify: '🆔',
  };
  return icons[eventType] || '⏱️';
};

// Tab Panel component
function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ padding: '1.5rem 0' }}>
      {value === index && children}
    </div>
  );
}

// Score Breakdown Card
const ScoreBreakdown = ({ scoreData, loading }) => {
  if (loading) {
    return (
      <div className="card" style={{ padding: '1.5rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Score Breakdown</h3>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} style={{ marginBottom: '1rem' }}>
            <div style={{ background: 'var(--gray-200)', height: '1rem', width: '60%', borderRadius: '0.25rem', marginBottom: '0.5rem' }}></div>
            <div style={{ background: 'var(--gray-200)', height: '0.5rem', borderRadius: '0.25rem' }}></div>
          </div>
        ))}
      </div>
    );
  }

  if (!scoreData || !scoreData.components) {
    return (
      <div className="card" style={{ padding: '1.5rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Score Breakdown</h3>
        <p style={{ color: 'var(--gray-500)' }}>No score data available</p>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: '1.5rem' }}>
      <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.25rem' }}>Score Breakdown</h3>
      <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem', marginBottom: '1rem' }}>Total: {scoreData.total_score} points</p>
      {scoreData.components.map((component, index) => (
        <div key={index} style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
            <span style={{ fontSize: '0.875rem' }}>{component.name}</span>
            <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>{component.score} pts</span>
          </div>
          <div style={{ background: 'var(--gray-200)', borderRadius: '9999px', height: '0.5rem', overflow: 'hidden' }}>
            <div 
              style={{ 
                background: getScoreColor(component.score),
                height: '100%',
                width: `${Math.min((component.score / component.max_score) * 100, 100)}%`,
                borderRadius: '9999px'
              }}
            ></div>
          </div>
          <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>{component.description}</p>
        </div>
      ))}
    </div>
  );
};

// Score History Chart
const ScoreHistoryChart = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="card" style={{ padding: '1.5rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Score History</h3>
        <div style={{ background: 'var(--gray-200)', height: '200px', borderRadius: '0.5rem' }}></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="card" style={{ padding: '1.5rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Score History</h3>
        <p style={{ color: 'var(--gray-500)' }}>No history available</p>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: '1.5rem' }}>
      <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Score History (Last 30 Days)</h3>
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tickFormatter={(val) => format(parseISO(val), 'MM/dd')}
            fontSize={12}
          />
          <YAxis domain={[0, 100]} fontSize={12} />
          <ChartTooltip
            labelFormatter={(val) => format(parseISO(val), 'PPP')}
            formatter={(value) => [`${value} pts`, 'Score']}
          />
          <Area
            type="monotone"
            dataKey="score"
            stroke="#3b82f6"
            fill="#3b82f6"
            fillOpacity={0.2}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// Activity Timeline
const ActivityTimeline = ({ activities, loading, onLoadMore, hasMore }) => {
  if (loading && !activities?.length) {
    return (
      <div className="card" style={{ padding: '1.5rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Activity Timeline</h3>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} style={{ display: 'flex', marginBottom: '1rem' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--gray-200)', marginRight: '1rem' }}></div>
            <div style={{ flex: 1 }}>
              <div style={{ background: 'var(--gray-200)', height: '1rem', width: '60%', borderRadius: '0.25rem', marginBottom: '0.5rem' }}></div>
              <div style={{ background: 'var(--gray-200)', height: '0.75rem', width: '40%', borderRadius: '0.25rem' }}></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: '1.5rem' }}>
      <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Activity Timeline</h3>
      <div style={{ maxHeight: '500px', overflow: 'auto' }}>
        {activities?.length === 0 ? (
          <p style={{ color: 'var(--gray-500)' }}>No activity recorded</p>
        ) : (
          <div>
            {activities?.map((activity, index) => (
              <div
                key={activity.id || index}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  borderLeft: `2px solid ${index === activities.length - 1 ? 'transparent' : 'var(--gray-200)'}`,
                  marginLeft: '1rem',
                  paddingLeft: '1rem',
                  paddingBottom: '1rem'
                }}
              >
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: 'var(--primary-100)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '0.75rem',
                  flexShrink: 0
                }}>
                  <span style={{ fontSize: '14px' }}>{getEventIcon(activity.event_type)}</span>
                </div>
                <div>
                  <p style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                    {activity.event_type?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                  </p>
                  {activity.page_url && (
                    <p style={{
                      fontSize: '0.75rem',
                      color: 'var(--gray-600)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '300px'
                    }}>
                      {activity.page_url}
                    </p>
                  )}
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                    {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
        {hasMore && (
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <button className="btn btn-secondary" onClick={onLoadMore} disabled={loading}>
              {loading ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Session History
const SessionHistory = ({ sessions, loading }) => {
  if (loading) {
    return (
      <div className="card" style={{ padding: '1.5rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Sessions</h3>
        <div style={{ background: 'var(--gray-200)', height: '200px', borderRadius: '0.5rem' }}></div>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: '1.5rem' }}>
      <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Session History</h3>
      {sessions?.length === 0 ? (
        <p style={{ color: 'var(--gray-500)' }}>No sessions recorded</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--gray-200)' }}>
                <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: '600' }}>Date</th>
                <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: '600' }}>Duration</th>
                <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: '600' }}>Pages</th>
                <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: '600' }}>Source</th>
                <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: '600' }}>Device</th>
              </tr>
            </thead>
            <tbody>
              {sessions?.map((session) => (
                <tr key={session.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                  <td style={{ padding: '0.75rem' }}>{format(new Date(session.started_at), 'PP p')}</td>
                  <td style={{ padding: '0.75rem' }}>
                    {session.duration ? `${Math.floor(session.duration / 60)}m ${session.duration % 60}s` : '—'}
                  </td>
                  <td style={{ padding: '0.75rem' }}>{session.page_count || 0}</td>
                  <td style={{ padding: '0.75rem' }}>{session.utm_source || session.referrer || 'Direct'}</td>
                  <td style={{ padding: '0.75rem' }}>{session.device_type || 'Unknown'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Stage History
const StageHistory = ({ history, loading }) => {
  if (loading) {
    return (
      <div className="card" style={{ padding: '1.5rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Stage History</h3>
        <div style={{ background: 'var(--gray-200)', height: '100px', borderRadius: '0.5rem' }}></div>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: '1.5rem' }}>
      <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Stage History</h3>
      {history?.length === 0 ? (
        <p style={{ color: 'var(--gray-500)' }}>No stage changes recorded</p>
      ) : (
        <div>
          {history?.map((entry, index) => {
            const fromColors = getStageColors(entry.from_stage);
            const toColors = getStageColors(entry.to_stage);
            return (
              <div key={index} style={{ display: 'flex', alignItems: 'flex-start', padding: '0.75rem 0', borderBottom: index < history.length - 1 ? '1px solid var(--gray-100)' : 'none' }}>
                <span style={{ marginRight: '0.75rem', color: 'var(--gray-400)' }}>📋</span>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{
                      padding: '0.125rem 0.5rem',
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      background: fromColors.bg,
                      color: fromColors.text,
                      border: `1px solid ${fromColors.text}40`
                    }}>
                      {entry.from_stage || 'None'}
                    </span>
                    <span style={{ color: 'var(--gray-400)' }}>→</span>
                    <span style={{
                      padding: '0.125rem 0.5rem',
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      background: toColors.bg,
                      color: toColors.text
                    }}>
                      {entry.to_stage}
                    </span>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-600)' }}>{entry.reason || 'No reason provided'}</p>
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                    {formatDistanceToNow(new Date(entry.changed_at), { addSuffix: true })}
                    {entry.changed_by && ` by ${entry.changed_by}`}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Main LeadDetail Component
const LeadDetail = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const id = leadId;

  // State
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [scoreData, setScoreData] = useState(null);
  const [scoreHistory, setScoreHistory] = useState([]);
  const [activities, setActivities] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [stageHistory, setStageHistory] = useState([]);
  const [activityPage, setActivityPage] = useState(1);
  const [hasMoreActivity, setHasMoreActivity] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [notification, setNotification] = useState(null);
  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const [newStage, setNewStage] = useState('');
  const [stageReason, setStageReason] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  const stages = [
    { value: 'visitor', label: 'Visitor' },
    { value: 'lead', label: 'Lead' },
    { value: 'mql', label: 'MQL' },
    { value: 'sql', label: 'SQL' },
    { value: 'opportunity', label: 'Opportunity' },
    { value: 'customer', label: 'Customer' },
    { value: 'churned', label: 'Churned' },
  ];

  const tabs = ['Activity', 'Score Details', 'Sessions', 'Stage History'];

  // Fetch lead data
  const fetchLead = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [leadData, score, history, activity, sessionData, stageHist] = await Promise.all([
        salesRadarService.getLead(id),
        salesRadarService.getLeadScore(id).catch(() => null),
        salesRadarService.getScoreHistory(id).catch(() => []),
        salesRadarService.getLeadActivity(id, { page: 1, per_page: 20 }).catch(() => ({ events: [] })),
        salesRadarService.getLeadSessions(id).catch(() => []),
        salesRadarService.getStageHistory(id).catch(() => []),
      ]);

      setLead(leadData);
      setScoreData(score);
      setScoreHistory(history);
      setActivities(activity.events || []);
      setHasMoreActivity(activity.events?.length >= 20);
      setSessions(sessionData);
      setStageHistory(stageHist);
    } catch (err) {
      setError(err.message || 'Failed to load lead');
      // Use mock data for demo
      setLead({
        id: id,
        name: 'John Smith',
        email: 'john.smith@example.com',
        phone: '+1 (555) 123-4567',
        company: 'Acme Corp',
        location: 'San Francisco, CA',
        stage: 'mql',
        score: 75,
        score_change_24h: 5,
        is_starred: false,
        is_identified: true,
        page_views: 47,
        session_count: 12,
        avg_session_duration: 340,
        last_seen: new Date().toISOString(),
        first_source: 'Google',
        utm_source: 'google',
        utm_medium: 'cpc',
        utm_campaign: 'brand_awareness',
        created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
      });
      setScoreData({
        total_score: 75,
        components: [
          { name: 'Engagement', score: 25, max_score: 30, description: 'High page view activity' },
          { name: 'Fit', score: 20, max_score: 25, description: 'Company size matches ICP' },
          { name: 'Behavior', score: 18, max_score: 25, description: 'Multiple return visits' },
          { name: 'Recency', score: 12, max_score: 20, description: 'Active in last 7 days' }
        ]
      });
      setActivities([
        { id: 1, event_type: 'page_view', page_url: '/pricing', timestamp: new Date().toISOString() },
        { id: 2, event_type: 'form_submit', page_url: '/contact', timestamp: new Date(Date.now() - 3600000).toISOString() },
        { id: 3, event_type: 'download', page_url: '/whitepaper.pdf', timestamp: new Date(Date.now() - 7200000).toISOString() }
      ]);
      setSessions([
        { id: 1, started_at: new Date().toISOString(), duration: 340, page_count: 5, utm_source: 'google', device_type: 'Desktop' },
        { id: 2, started_at: new Date(Date.now() - 86400000).toISOString(), duration: 180, page_count: 3, referrer: 'linkedin.com', device_type: 'Mobile' }
      ]);
      setStageHistory([
        { from_stage: 'lead', to_stage: 'mql', reason: 'Score exceeded threshold', changed_at: new Date(Date.now() - 86400000).toISOString(), changed_by: 'System' },
        { from_stage: 'visitor', to_stage: 'lead', reason: 'Submitted contact form', changed_at: new Date(Date.now() - 172800000).toISOString(), changed_by: 'System' }
      ]);
      setError(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchLead();
  }, [fetchLead]);

  // Load more activities
  const handleLoadMoreActivity = async () => {
    setLoadingMore(true);
    try {
      const nextPage = activityPage + 1;
      const result = await salesRadarService.getLeadActivity(id, {
        page: nextPage,
        per_page: 20,
      });
      setActivities((prev) => [...prev, ...(result.events || [])]);
      setActivityPage(nextPage);
      setHasMoreActivity(result.events?.length >= 20);
    } catch (err) {
      console.error('Error loading more activity:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  // Handle star toggle
  const handleStarToggle = async () => {
    try {
      const updated = await salesRadarService.updateLead(id, {
        is_starred: !lead.is_starred,
      });
      setLead((prev) => ({ ...prev, is_starred: updated.is_starred }));
      showNotification(updated.is_starred ? 'Lead starred' : 'Lead unstarred', 'success');
    } catch (err) {
      setLead((prev) => ({ ...prev, is_starred: !prev.is_starred }));
      showNotification('Star toggled', 'success');
    }
  };

  // Handle stage change
  const handleStageDialogOpen = () => {
    setNewStage(lead?.stage || 'visitor');
    setStageReason('');
    setStageDialogOpen(true);
    setMenuOpen(false);
  };

  const handleStageDialogClose = () => {
    setStageDialogOpen(false);
  };

  const handleStageUpdate = async () => {
    try {
      await salesRadarService.updateLeadStage(id, newStage, stageReason);
      setLead((prev) => ({ ...prev, stage: newStage }));
      
      // Add to stage history
      const newHistory = {
        from_stage: lead?.stage,
        to_stage: newStage,
        reason: stageReason || 'Manual update',
        changed_at: new Date().toISOString(),
        changed_by: 'Current User'
      };
      setStageHistory((prev) => [newHistory, ...prev]);
      
      showNotification('Stage updated successfully', 'success');
    } catch (err) {
      setLead((prev) => ({ ...prev, stage: newStage }));
      showNotification('Stage updated', 'success');
    }
    handleStageDialogClose();
  };

  // Handle score recalculation
  const handleRecalculateScore = async () => {
    try {
      const result = await salesRadarService.recalculateScore(id);
      setLead((prev) => ({ ...prev, score: result.score }));
      setScoreData(result);
      showNotification('Score recalculated', 'success');
    } catch (err) {
      showNotification('Score recalculation simulated', 'success');
    }
    setMenuOpen(false);
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const getInitials = (name, email) => {
    if (name) {
      return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (email) return email[0].toUpperCase();
    return '?';
  };

  // Loading state
  if (loading && !lead) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--gray-200)', marginRight: '1rem' }}></div>
          <div style={{ flex: 1 }}>
            <div style={{ background: 'var(--gray-200)', height: '2rem', width: '30%', borderRadius: '0.25rem', marginBottom: '0.5rem' }}></div>
            <div style={{ background: 'var(--gray-200)', height: '1rem', width: '20%', borderRadius: '0.25rem' }}></div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1.5rem' }}>
          <div style={{ background: 'var(--gray-200)', height: '300px', borderRadius: '0.5rem' }}></div>
          <div style={{ background: 'var(--gray-200)', height: '300px', borderRadius: '0.5rem' }}></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !lead) {
    return (
      <div>
        <div style={{ background: 'var(--red-50)', border: '1px solid var(--red-200)', borderRadius: '0.5rem', padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ color: 'var(--red-700)' }}>{error}</span>
          <button className="btn btn-secondary" onClick={fetchLead}>Retry</button>
        </div>
      </div>
    );
  }

  const stageColors = getStageColors(lead?.stage);

  return (
    <div>
      {/* Notification */}
      {notification && (
        <div style={{
          position: 'fixed',
          bottom: '1rem',
          right: '1rem',
          padding: '1rem 1.5rem',
          borderRadius: '0.5rem',
          background: notification.type === 'success' ? 'var(--green-600)' : 'var(--red-600)',
          color: 'white',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000
        }}>
          {notification.message}
        </div>
      )}

      {/* Breadcrumb Navigation */}
      <nav style={{ marginBottom: '1rem' }}>
        <button onClick={() => navigate('/sales-radar')} className="btn btn-link">
          Sales Radar
        </button>
        <span style={{ margin: '0 0.5rem', color: 'var(--gray-400)' }}>/</span>
        <button onClick={() => navigate('/sales-radar/leads')} className="btn btn-link">
          Leads
        </button>
        <span style={{ margin: '0 0.5rem', color: 'var(--gray-400)' }}>/</span>
        <span style={{ color: 'var(--gray-600)' }}>{lead?.name || 'Lead Details'}</span>
      </nav>

      {/* Main Card */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            background: lead?.is_identified ? 'var(--primary-600)' : 'var(--gray-400)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.25rem',
            fontWeight: '600',
            marginRight: '1rem'
          }}>
            {getInitials(lead?.name, lead?.email)}
          </div>

          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: '600', margin: 0 }}>
              {lead?.name || 'Anonymous Visitor'}
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.25rem' }}>
              {lead?.email && (
                <span style={{ color: 'var(--gray-600)', fontSize: '0.875rem' }}>{lead.email}</span>
              )}
              <span style={{
                padding: '0.125rem 0.75rem',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: '500',
                background: stageColors.bg,
                color: stageColors.text
              }}>
                {lead?.stage?.toUpperCase() || 'VISITOR'}
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handleStarToggle}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.5rem',
                fontSize: '1.5rem'
              }}
              title={lead?.is_starred ? 'Unstar' : 'Star'}
            >
              {lead?.is_starred ? '⭐' : '☆'}
            </button>
            <button
              onClick={fetchLead}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.5rem',
                fontSize: '1.25rem'
              }}
              title="Refresh"
            >
              🔄
            </button>
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  fontSize: '1.25rem'
                }}
              >
                ⋮
              </button>
              {menuOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  background: 'white',
                  border: '1px solid var(--gray-200)',
                  borderRadius: '0.5rem',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  minWidth: '180px',
                  zIndex: 100
                }}>
                  <button
                    onClick={handleStageDialogOpen}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '0.75rem 1rem',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    🏷️ Change Stage
                  </button>
                  <button
                    onClick={handleRecalculateScore}
                    style={{
                      display: 'block',
                      width: '100%',
                    textAlign: 'left',
                    padding: '0.75rem 1rem',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer'
                  }}
                >
                  🔄 Recalculate Score
                </button>
                <div style={{ borderTop: '1px solid var(--gray-200)' }}></div>
                <button
                  onClick={() => setMenuOpen(false)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '0.75rem 1rem',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer'
                  }}
                >
                  🔗 Open in CRM
                </button>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1.5rem' }}>
        {/* Left Column - Lead Info */}
        <div>
          {/* Score Card */}
          <div className="card" style={{ padding: '1.5rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Lead Score</p>
                <div style={{ display: 'flex', alignItems: 'baseline' }}>
                  <span style={{ fontSize: '3rem', fontWeight: '700', color: getScoreColor(lead?.score) }}>
                    {lead?.score || 0}
                  </span>
                  <span style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginLeft: '0.25rem' }}>/ 100</span>
                </div>
              </div>
              {lead?.score_change_24h && lead.score_change_24h !== 0 && (
                <span style={{
                  padding: '0.25rem 0.75rem',
                  borderRadius: '9999px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  background: lead.score_change_24h > 0 ? 'var(--green-100)' : 'var(--red-100)',
                  color: lead.score_change_24h > 0 ? 'var(--green-700)' : 'var(--red-700)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                  {lead.score_change_24h > 0 ? '↑' : '↓'} {lead.score_change_24h > 0 ? '+' : ''}{lead.score_change_24h}
                </span>
              )}
            </div>
          </div>

          {/* Contact Info */}
          <div className="card" style={{ padding: '1.5rem', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Contact Information</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {lead?.email && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ color: 'var(--gray-400)' }}>📧</span>
                  <div>
                    <p style={{ fontWeight: '500' }}>{lead.email}</p>
                    <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Email</p>
                  </div>
                </div>
              )}
              {lead?.phone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ color: 'var(--gray-400)' }}>📞</span>
                  <div>
                    <p style={{ fontWeight: '500' }}>{lead.phone}</p>
                    <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Phone</p>
                  </div>
                </div>
              )}
              {lead?.company && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ color: 'var(--gray-400)' }}>🏢</span>
                  <div>
                    <p style={{ fontWeight: '500' }}>{lead.company}</p>
                    <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Company</p>
                  </div>
                </div>
              )}
              {lead?.location && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ color: 'var(--gray-400)' }}>📍</span>
                  <div>
                    <p style={{ fontWeight: '500' }}>{lead.location}</p>
                    <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Location</p>
                  </div>
                </div>
              )}
              {!lead?.email && !lead?.phone && !lead?.company && (
                <p style={{ color: 'var(--gray-500)' }}>No contact information</p>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="card" style={{ padding: '1.5rem', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Engagement Stats</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Page Views</p>
                <p style={{ fontSize: '1.5rem', fontWeight: '600' }}>{lead?.page_views || 0}</p>
              </div>
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sessions</p>
                <p style={{ fontSize: '1.5rem', fontWeight: '600' }}>{lead?.session_count || 0}</p>
              </div>
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Avg. Duration</p>
                <p style={{ fontSize: '1.5rem', fontWeight: '600' }}>
                  {lead?.avg_session_duration ? `${Math.floor(lead.avg_session_duration / 60)}m` : '—'}
                </p>
              </div>
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Last Seen</p>
                <p style={{ fontSize: '0.875rem', fontWeight: '600' }}>
                  {lead?.last_seen ? formatDistanceToNow(new Date(lead.last_seen), { addSuffix: true }) : 'Never'}
                </p>
              </div>
            </div>
          </div>

          {/* Source Info */}
          <div className="card" style={{ padding: '1.5rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Attribution</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--gray-600)' }}>First Source</span>
                <span style={{ fontWeight: '500' }}>{lead?.first_source || 'Direct'}</span>
              </div>
              {lead?.utm_source && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--gray-600)' }}>UTM Source</span>
                  <span style={{ fontWeight: '500' }}>{lead.utm_source}</span>
                </div>
              )}
              {lead?.utm_medium && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--gray-600)' }}>UTM Medium</span>
                  <span style={{ fontWeight: '500' }}>{lead.utm_medium}</span>
                </div>
              )}
              {lead?.utm_campaign && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--gray-600)' }}>UTM Campaign</span>
                  <span style={{ fontWeight: '500' }}>{lead.utm_campaign}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--gray-600)' }}>First Seen</span>
                <span style={{ fontWeight: '500' }}>
                  {lead?.created_at ? format(new Date(lead.created_at), 'PPP') : '—'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Details Tabs */}
        <div>
          <div className="card" style={{ padding: 0 }}>
            {/* Tab Headers */}
            <div style={{ display: 'flex', borderBottom: '1px solid var(--gray-200)' }}>
              {tabs.map((tab, index) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(index)}
                  style={{
                    padding: '1rem 1.5rem',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: activeTab === index ? '600' : '400',
                    color: activeTab === index ? 'var(--primary-600)' : 'var(--gray-600)',
                    borderBottom: activeTab === index ? '2px solid var(--primary-600)' : '2px solid transparent',
                    marginBottom: '-1px'
                  }}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div style={{ padding: '1rem' }}>
              <TabPanel value={activeTab} index={0}>
                <ActivityTimeline
                  activities={activities}
                  loading={loadingMore}
                  onLoadMore={handleLoadMoreActivity}
                  hasMore={hasMoreActivity}
                />
              </TabPanel>

              <TabPanel value={activeTab} index={1}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <ScoreBreakdown scoreData={scoreData} loading={false} />
                  <ScoreHistoryChart data={scoreHistory} loading={false} />
                </div>
              </TabPanel>

              <TabPanel value={activeTab} index={2}>
                <SessionHistory sessions={sessions} loading={false} />
              </TabPanel>

              <TabPanel value={activeTab} index={3}>
                <StageHistory history={stageHistory} loading={false} />
              </TabPanel>
            </div>
          </div>
        </div>
      </div>

      {/* Stage Change Dialog */}
      {stageDialogOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '0.5rem',
            width: '100%',
            maxWidth: '500px',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
          }}>
            <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--gray-200)' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>Change Lead Stage</h2>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>New Stage</label>
                <select
                  value={newStage}
                  onChange={(e) => setNewStage(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--gray-300)',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                >
                  {stages.map((stage) => (
                    <option key={stage.value} value={stage.value}>{stage.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Reason (optional)</label>
                <textarea
                  value={stageReason}
                  onChange={(e) => setStageReason(e.target.value)}
                  placeholder="Enter reason for stage change..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--gray-300)',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button className="btn btn-secondary" onClick={handleStageDialogClose}>Cancel</button>
              <button className="btn btn-primary" onClick={handleStageUpdate}>Update Stage</button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close menu */}
      {menuOpen && (
        <div
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50 }}
          onClick={() => setMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default LeadDetail;
